import { c as _c } from "react/compiler-runtime";
import { disableBasedOnValueKey } from '@onefootprint/core';
import { getSessionId } from '@onefootprint/dev-tools';
import type { ApiError } from '@onefootprint/request-types';
import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import applyCaseMiddleware from 'axios-case-converter';
import { useTranslation } from 'react-i18next';
type Obj = Record<string, unknown>;
/** @deprecated Use `ApiError` from `@onefootprint/request-types` instead. */
type RequestError = AxiosError<FootprintServerError>;
/** @deprecated Use `ApiError` from `@onefootprint/request-types` instead. */
type FootprintServerError = {
  message: string;
  code?: string; // Translation code for resolving the error to the correct language
  context?: Record<string, string>; // Additional data needed to resolve the error string
  statusCode: number;
  supportId: string;
};

/** @deprecated Use types from `@onefootprint/request-types` instead. */
type PaginatedRequestResponse<T> = {
  data: T;
  meta: {
    next?: string | null;
    nextPage?: string | null;
    count: number;
  };
};
const uuidPattern = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/g;
const clientVersion = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ? `frontend:${process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA}` : undefined;
const LOGOUT_ERROR_CODES = ['E117', 'E118', 'E119'];
const isObject = (x: unknown): x is Obj => typeof x === 'object' && !!x;
const isString = (x: unknown): x is string => typeof x === 'string' && !!x;

/** @deprecated Use `isApiError` from `@onefootprint/fetch-hosted` or `@onefootprint/fetch-dashboard` instead. */
const isFootprintError = (err: unknown): err is RequestError | ApiError => {
  if (!err || typeof err !== 'object') {
    return false;
  }
  return 'response' in err && typeof err.response === 'object' && err.response != null && 'data' in err.response || 'status' in err && 'message' in err;
};

/** @deprecated Use error utilities from `@onefootprint/fetch-hosted` or `@onefootprint/fetch-dashboard` instead. */
const isUnhandledError = (err: unknown): err is Error => typeof err === 'object' && err != null && 'message' in err && typeof err.message === 'string';

/** @deprecated Use `isLogoutError` from `@onefootprint/fetch-hosted` or `@onefootprint/fetch-dashboard` instead. */
const isLogoutError = (error: unknown) => {
  if (!isFootprintError(error)) {
    return undefined;
  }
  const err = (error as RequestError)?.response?.data as FootprintServerError;
  return LOGOUT_ERROR_CODES.some(code => (err?.code ?? (error as ApiError)?.code) === code);
};
const extractStringProps = (x: unknown, acc: string[] = []): string[] => {
  const stack = [x];
  while (stack.length > 0) {
    const current = stack.pop();
    if (isString(current)) {
      acc.push(current);
    } else if (isObject(current)) {
      for (const key in current) {
        if (Object.hasOwn(current, key)) {
          stack.push(current[key]);
        }
      }
    }
  }
  return acc;
};

/** @deprecated Use `getErrorMessage` from `@onefootprint/fetch-hosted` or `@onefootprint/fetch-dashboard` instead. */
const getErrorMessage = (error?: unknown | Error): string => {
  if (isString(error)) {
    return error.trim();
  }
  if (isFootprintError(error)) {
    return (error as RequestError)?.response?.data?.message?.trim() ?? (error as ApiError)?.message?.trim() ?? 'Something went wrong';
  }
  if (isUnhandledError(error) && isString(error.message)) {
    return error.message.trim();
  }
  return extractStringProps(error).filter(x => !x.match(uuidPattern)).join('').trim() || 'Something went wrong';
};

/** @deprecated Use `getErrorCode` from `@onefootprint/fetch-hosted` or `@onefootprint/fetch-dashboard` instead. */
const getErrorCode = (error?: unknown | Error): string | undefined => {
  if (!error || !isFootprintError(error)) {
    return undefined;
  }
  const data = (error as RequestError)?.response?.data;
  const errorCode = data?.code ?? (error as ApiError)?.code;
  if (errorCode) {
    return errorCode;
  }
  return undefined;
};

/** @deprecated Use `useRequestError` from `@onefootprint/fetch-hosted` or `@onefootprint/fetch-dashboard` instead. */
const useRequestError = () => {
  const $0 = _c(6);
  let t0;
  if ($0[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "errors"
    };
    $0[0] = t0;
  } else {
    t0 = $0[0];
  }
  const {
    t
  } = useTranslation("request", t0);
  let t1;
  if ($0[1] !== t) {
    t1 = code => !!code?.match(/^E[0-9]+$/g) && !!t($ => $[code as keyof typeof $], {
      defaultValue: ""
    });
    $0[1] = t;
    $0[2] = t1;
  } else {
    t1 = $0[2];
  }
  const isValidErrorCode = t1;
  const getContext = _temp;
  let t2;
  if ($0[3] !== isValidErrorCode || $0[4] !== t) {
    const getMessage = error_0 => {
      if (isString(error_0)) {
        return error_0;
      }
      const unknownError = t(_temp2);
      if (isFootprintError(error_0)) {
        const data_0 = (error_0 as RequestError)?.response?.data;
        const errorCode = data_0?.code ?? (error_0 as ApiError)?.code;
        const errorContext_0 = data_0?.context ?? (error_0 as ApiError)?.context;
        const errorMessage = data_0?.message ?? (error_0 as ApiError)?.message;
        if (!errorCode || !isValidErrorCode(errorCode)) {
          return errorMessage ?? unknownError;
        }
        if (!errorContext_0) {
          return t($_1 => $_1[errorCode as keyof typeof $_1]) ?? unknownError;
        }
        return t($_2 => $_2[errorCode as keyof typeof $_2], errorContext_0) ?? unknownError;
      }
      if (isUnhandledError(error_0)) {
        return error_0.message;
      }
      return unknownError;
    };
    const getStatusCode = _temp3;
    t2 = {
      getErrorMessage: getMessage,
      getErrorCode,
      getErrorContext: getContext,
      getErrorStatusCode: getStatusCode
    };
    $0[3] = isValidErrorCode;
    $0[4] = t;
    $0[5] = t2;
  } else {
    t2 = $0[5];
  }
  return t2;
};
type RequestDefaults = {
  withCredentials?: boolean;
  headers?: Record<string, string>;
};
let defaults: RequestDefaults = {};

// The API allows credentials only from the dashboard's origins, so any other app sending them fails CORS.
const configure = (next: RequestDefaults) => {
  defaults = next;
};
type ResponseErrorHandler = (error: AxiosError) => void;
const responseErrorHandlers = new Set<ResponseErrorHandler>();

/** Shows `handler` every failed response before its caller sees it. Returns a function that removes it. */
const onResponseError = (handler: ResponseErrorHandler) => {
  responseErrorHandlers.add(handler);
  return () => {
    responseErrorHandlers.delete(handler);
  };
};

// Every call builds its own axios instance, so each one gets the interceptor.
const withResponseErrorHandlers = (client: AxiosInstance) => {
  client.interceptors.response.use(undefined, (error: AxiosError) => {
    for (const handler of responseErrorHandlers) {
      handler(error);
    }
    return Promise.reject(error);
  });
  return client;
};

/** @deprecated Use the fetch client from `@onefootprint/fetch-hosted` or `@onefootprint/fetch-dashboard` instead. */
const getRequestOptions = (requestConfig: AxiosRequestConfig, extraOptions: {
  omitSessionId?: boolean;
  omitClientVersion?: boolean;
} = {}) => {
  const sessionId = getSessionId();
  return {
    baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
    timeout: 60000,
    withCredentials: defaults.withCredentials ?? false,
    ...requestConfig,
    headers: {
      ...defaults.headers,
      'x-fp-session-id': extraOptions.omitSessionId ? undefined : sessionId,
      'x-fp-client-version': extraOptions.omitClientVersion ? undefined : clientVersion,
      ...requestConfig.headers
    }
  };
};

/** @deprecated Use the fetch client from `@onefootprint/fetch-hosted` or `@onefootprint/fetch-dashboard` instead. */
const request = <Response = unknown,>(requestConfig: AxiosRequestConfig = {}, extraOptions: {
  omitSessionId?: boolean;
  omitClientVersion?: boolean;
} = {}) => {
  const client = withResponseErrorHandlers(applyCaseMiddleware(axios.create(), {
    preservedKeys: disableBasedOnValueKey
  }));
  const options = getRequestOptions(requestConfig, extraOptions);
  return client.request<Response>(options);
};

/** @deprecated Use the fetch client from `@onefootprint/fetch-hosted` or `@onefootprint/fetch-dashboard` instead. */
const requestWithoutCaseConverter = <Response = unknown,>(requestConfig: AxiosRequestConfig = {}) => {
  const client = withResponseErrorHandlers(axios.create());
  const options = getRequestOptions(requestConfig);
  return client.request<Response>(options);
};
export type { FootprintServerError, PaginatedRequestResponse, RequestError };
export { axios as baseRequest, configure, getErrorCode, getErrorMessage, getRequestOptions, isFootprintError, isLogoutError, isUnhandledError, onResponseError, requestWithoutCaseConverter, useRequestError };
export default request;
function _temp(error) {
  if (isFootprintError(error)) {
    const data = (error as RequestError)?.response?.data;
    const errorContext = data?.context ?? (error as ApiError)?.context;
    return errorContext || {};
  }
  return {};
}
function _temp2($_0) {
  return $_0.unknown;
}
function _temp3(error_1) {
  if (!error_1 || !isFootprintError(error_1)) {
    return;
  }
  const statusCode = (error_1 as RequestError)?.response?.status ?? (error_1 as ApiError)?.status;
  if (statusCode) {
    return statusCode;
  }
}